import { useState } from 'react';
import { initialFormValues, type WarehouseFormValues } from './schemas';
import { KhoHang, KhoHangInput } from '@/types/schemas/kho-hang.type';
import { ActionBar, DeleteDialog, FormDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { warehouseColumns } from './cols-definition';
import { usePathname, useRouter } from '@/routing';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function KhoHangPage() {
  const [copiedWarehouse, setCopiedWarehouse] = useState<KhoHang | null>(null);
  const [activeTab, setActiveTab] = useState<string>('thong tin');
  const pathName = usePathname();
  const router = useRouter();

  const {
    data: warehouses,
    isLoading,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addWarehouse,
    updateItem: updateWarehouse,
    deleteItem: deleteWarehouse,
    refreshData: refreshWarehouses,
    searchData
  } = useCRUD<KhoHang, KhoHangInput>({
    endpoint: QUERY_KEYS.KHO_HANG,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj: selectedWarehouse,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<KhoHang>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : warehouses || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const handleCopyForm = (warehouse: KhoHang) => {
    setCopiedWarehouse(warehouse);
    handleCopyClick();
  };

  const handleAddWarehouse = () => {
    setCopiedWarehouse(null);
    handleAddClick();
  };

  const handleChangeTab = (newValue: string) => {
    setActiveTab(newValue);
  };

  const handleTabChange = (tabPath: string) => {
    if (tabPath === `/danh-muc/vat-tu-san-pham-va-kho/${pathName}`) return;
    router.push(`/danh-muc/vat-tu-san-pham-va-kho/${tabPath}`);
  };

  const handleFormSubmit = async (data: WarehouseFormValues) => {
    try {
      if (formMode === 'add') {
        await addWarehouse({
          unit_id: data.unit_id || null,
          ma_unit: data.ma_unit || '',
          ma_kho: data.ma_kho || '',
          ten_kho: data.ten_kho || '',
          ten_kho2: data.ten_kho2 || null,
          vi_tri_yn: data.vi_tri_yn || false,
          dai_ly_yn: data.dai_ly_yn || false,
          ma_nh: data.ma_nh || null,
          dia_chi: data.dia_chi || '',
          dien_thoai: data.dien_thoai || '',
          ghi_chu: data.ghi_chu || null,
          status: data.status || '1',
          entity_model: data.entity_model || ''
        });
      } else if (formMode === 'edit' && selectedWarehouse) {
        await updateWarehouse(selectedWarehouse.uuid, {
          unit_id: data.unit_id || null,
          ma_unit: data.ma_unit || '',
          ma_kho: data.ma_kho || '',
          ten_kho: data.ten_kho || '',
          ten_kho2: data.ten_kho2 || null,
          vi_tri_yn: data.vi_tri_yn || false,
          dai_ly_yn: data.dai_ly_yn || false,
          ma_nh: data.ma_nh || null,
          dia_chi: data.dia_chi || '',
          dien_thoai: data.dien_thoai || '',
          ghi_chu: data.ghi_chu || null,
          status: data.status || '1',
          entity_model: data.entity_model || ''
        });
      }
      refreshWarehouses();
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const tables = [
    {
      name: 'Vật tư, sản phẩm',
      rows: [],
      columns: [],
      value: 0
    },
    {
      name: 'Lô hàng',
      rows: [],
      columns: [],
      value: 1
    },
    {
      name: 'Kho hàng',
      rows: displayData,
      columns: warehouseColumns(),
      value: 2
    },
    {
      name: 'Vị trí kho hàng',
      rows: [],
      columns: [],
      value: 3
    }
  ];

  const formInitialData =
    formMode === 'add'
      ? copiedWarehouse || isCopyMode
        ? copiedWarehouse
          ? {
              unit_id: copiedWarehouse.unit_id,
              ma_unit: copiedWarehouse.ma_unit,
              unit_name: '',
              ma_kho: copiedWarehouse.ma_kho,
              ten_kho: copiedWarehouse.ten_kho,
              ten_kho2: copiedWarehouse.ten_kho2,
              vi_tri_yn: copiedWarehouse.vi_tri_yn,
              dai_ly_yn: copiedWarehouse.dai_ly_yn,
              ma_nh: copiedWarehouse.ma_nh,
              dia_chi: copiedWarehouse.dia_chi,
              dien_thoai: copiedWarehouse.dien_thoai,
              ghi_chu: copiedWarehouse.ghi_chu,
              status: copiedWarehouse.status,
              entity_model: copiedWarehouse.entity_model
            }
          : detail
        : initialFormValues
      : detail;

  return (
    <div className='flex h-screen w-full min-w-0 max-w-full flex-col overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          mode={formMode}
          initialData={formInitialData}
          activeTab={activeTab}
          onTabChange={handleChangeTab}
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            handleAddWarehouse();
          }}
          onEdit={() => {
            if (selectedWarehouse) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedWarehouse) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedWarehouse) {
              handleCloseForm();
              handleCopyForm(selectedWarehouse);
            }
          }}
        />
      )}

      {!showForm && (
        <div className='flex h-full overflow-hidden'>
          <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
            <ActionBar
              onAddClick={handleAddWarehouse}
              onEditClick={() => selectedWarehouse && handleEditClick()}
              onDeleteClick={() => selectedWarehouse && handleDeleteClick()}
              onCopyClick={() => selectedWarehouse && handleCopyForm(selectedWarehouse)}
              onViewClick={() => selectedWarehouse && handleViewClick()}
              onSearchClick={() => {}}
              onRefreshClick={refreshWarehouses}
              onPrintClick={() => {}}
              isEditDisabled={!selectedWarehouse}
            />

            <div className='min-w-0 flex-1 overflow-auto'>
              <ErpDataTableV2
                tables={tables}
                onRowClick={handleRowClick}
                selectedRowId={selectedRowIndex || undefined}
                defaultTabIndex={2}
                onTabChange={(_, value: number) => {
                  const tabPaths = ['vat-tu-san-pham', 'lo-hang', 'kho-hang', 'vi-tri-kho-hang'];
                  handleTabChange(tabPaths[value]);
                }}
                totalItems={resolvedTotalItems}
                currentPage={resolvedCurrentPage}
                onPageChange={onPageChange}
                serverSidePagination={true}
                pageSize={PAGE_SIZE}
                onSearchChange={handleSearchChange}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedWarehouse={selectedWarehouse}
          deleteWarehouse={deleteWarehouse}
          refreshWarehouses={refreshWarehouses}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
